import { z } from "zod";
import { isPhoneComplete } from "@/shared/utils";

function parseTimeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}

const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;

export function createImportWorkerSchema(draftFilenames: Record<string, string> = {}) {
  function hasFile(value: unknown, fieldName: string): boolean {
    return value instanceof File || draftFilenames[fieldName] != null;
  }

  function hasMultiFile(values: unknown[], fieldName: string): boolean {
    if (values.some((f) => f instanceof File)) return true;
    return Object.keys(draftFilenames).some((k) => k.startsWith(fieldName));
  }

  return z.object({
    iin: z
      .string()
      .min(12, "validation.iin.length")
      .max(12, "validation.iin.length")
      .regex(/^\d{12}$/, "validation.iin.digitsOnly"),
    surname: z.string().min(1, "validation.required"),
    name: z.string().min(1, "validation.required"),
    father_name: z.string(),
    date_of_birth: z
      .date({ error: "validation.required" })
      .nullable()
      .refine((v) => v !== null, { message: "validation.required" }),
    gender: z.enum(["male", "female", ""], { error: "validation.required" }).refine((v) => v !== "", {
      message: "validation.required",
    }),
    nationality: z.string().min(1, "validation.required"),
    citizenship: z.string(),
    is_resident: z.boolean(),
    is_student: z.boolean(),
    enrollment_start_date: z.date({ error: "validation.required" }).nullable(),
    enrollment_end_date: z.date({ error: "validation.required" }).nullable(),
    family_status: z.enum(["single", "married", "divorced", "widowed", ""]),
    email: z.string().min(1, "validation.required").email("validation.invalidEmail"),
    phone: z
      .string()
      .min(1, "validation.required")
      .refine(isPhoneComplete, "validation.invalidPhone"),
    sign_date: z
      .date({ error: "validation.required" })
      .nullable()
      .refine((v) => v !== null, { message: "validation.required" }),
    start_date: z
      .date({ error: "validation.required" })
      .nullable()
      .refine((v) => v !== null, { message: "validation.required" }),
    contract_number: z
      .string()
      .optional()
      .refine((v) => !v || (/^\d{1,18}$/.test(v) && Number(v) > 0), { message: "validation.contract_number_invalid" }),
    work_city_id: z.number().nullable(),
    region_id: z.number().optional(),
    employee_address_ru: z.string().min(1, "validation.required"),
    employee_address_kk: z.string().min(1, "validation.required"),
    job_position_ru: z.string().min(1, "validation.required"),
    job_position_kk: z.string().min(1, "validation.required"),
    job_duties_ru: z.array(z.string()),
    job_duties_kk: z.array(z.string()),
    working_days_list: z
      .array(z.enum(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]))
      .min(1, "validation.required")
      .max(6, "validation.workingDaysExactlyFive"),
    work_start_time: z.string().min(1, "validation.required").regex(timeRegex, "validation.invalidTimeFormat"),
    work_end_time: z.string().min(1, "validation.required").regex(timeRegex, "validation.invalidTimeFormat"),
    has_break: z.boolean(),
    break_start_time: z.string(),
    break_end_time: z.string(),
    work_format: z.enum(["onsite", "remote", "hybrid", ""]),
    salary_amount: z.string().min(1, "validation.required"),
    trial_period: z.boolean(),
    trial_duration_months: z.number().optional(),
    current_vacation_days: z.string().min(1, "validation.required"),
    bank_name: z.string().min(1, "validation.required"),
    iban_account: z
      .string()
      .min(1, "validation.required")
      .length(20, "validation.iban.length"),
    bik_number: z
      .string()
      .min(1, "validation.required")
      .min(8, "validation.bik.length")
      .max(11, "validation.bik.length"),
    photo_file: z.any().nullable(),
    national_id_file: z.any().nullable(),
    military_certificate_file: z.any().nullable(),
    bank_certificate_file: z.any().nullable(),
    enrollment_verification_file: z.any().nullable(),
    work_proof_file: z.any().nullable(),
    enpf_certificate_file: z.any().nullable(),
    diploma_file: z.array(z.any()),
    diploma_transcript_file: z.array(z.any()),
    social_category_document_file: z.array(z.any()),
    contract_file: z.any().nullable(),
    job_application_file: z.any().nullable(),
    order_on_hiring_file: z.any().nullable(),
    personal_sheet_file: z.any().nullable(),
    has_no_education: z.boolean(),
    has_no_work_experience: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.salary_amount && isNaN(Number(data.salary_amount))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.salary.positive",
        path: ["salary_amount"],
      });
    } else if (data.salary_amount && Number(data.salary_amount) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.salary.positive",
        path: ["salary_amount"],
      });
    }

    if (data.sign_date && data.start_date && data.start_date < data.sign_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.startDateBeforeSign",
        path: ["start_date"],
      });
    }

    if (data.job_duties_ru.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.dutiesMinOne",
        path: ["job_duties_ru"],
      });
    }

    if (data.job_duties_kk.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.dutiesMinOne",
        path: ["job_duties_kk"],
      });
    }

    if (data.job_duties_ru.length !== data.job_duties_kk.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.dutiesEqualLength",
        path: ["job_duties_kk"],
      });
    }

    if (data.has_break) {
      if (!data.break_start_time || !timeRegex.test(data.break_start_time)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.breakRequired",
          path: ["break_start_time"],
        });
      }
      if (!data.break_end_time || !timeRegex.test(data.break_end_time)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.breakRequired",
          path: ["break_end_time"],
        });
      }
    }

    if (
      data.has_break &&
      data.break_start_time &&
      data.break_end_time &&
      data.work_start_time &&
      data.work_end_time &&
      timeRegex.test(data.break_start_time) &&
      timeRegex.test(data.break_end_time) &&
      timeRegex.test(data.work_start_time) &&
      timeRegex.test(data.work_end_time)
    ) {
      const workStart = parseTimeToMinutes(data.work_start_time);
      const workEnd = parseTimeToMinutes(data.work_end_time);
      const breakStart = parseTimeToMinutes(data.break_start_time);
      const breakEnd = parseTimeToMinutes(data.break_end_time);
      if (!(workStart < breakStart && breakStart < breakEnd && breakEnd < workEnd)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.breakWithinWorkHours",
          path: ["break_start_time"],
        });
      }
    }

    if (data.work_start_time && data.work_end_time && timeRegex.test(data.work_start_time) && timeRegex.test(data.work_end_time)) {
      const workStart = parseTimeToMinutes(data.work_start_time);
      const workEnd = parseTimeToMinutes(data.work_end_time);
      if (workEnd <= workStart) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.workEndAfterStart",
          path: ["work_end_time"],
        });
      } else {
        let workingMinutes = workEnd - workStart;
        if (data.has_break && data.break_start_time && data.break_end_time && timeRegex.test(data.break_start_time) && timeRegex.test(data.break_end_time)) {
          const breakStart = parseTimeToMinutes(data.break_start_time);
          const breakEnd = parseTimeToMinutes(data.break_end_time);
          workingMinutes -= breakEnd - breakStart;
        }
        if (workingMinutes !== 240 && workingMinutes !== 480) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.workingHoursFourOrEight",
            path: ["work_end_time"],
          });
        }
      }
    }

    if (data.working_days_list.length !== 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.workingDaysExactlyFive",
        path: ["working_days_list"],
      });
    }

    if (!data.work_format) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.workFormatRequired",
        path: ["work_format"],
      });
    }

    if (data.region_id == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.regionRequired",
        path: ["region_id"],
      });
    }

    const ALWAYS_REQUIRED_FILES = [
      "photo_file", "national_id_file", "bank_certificate_file",
      "contract_file", "job_application_file", "order_on_hiring_file",
    ] as const;

    for (const field of ALWAYS_REQUIRED_FILES) {
      if (!hasFile(data[field], field)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.fileRequired", path: [field] });
      }
    }

    if (!data.has_no_work_experience) {
      if (!hasFile(data.work_proof_file, "work_proof_file")) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.fileRequired", path: ["work_proof_file"] });
      }
    }

    if (data.gender === "male" && !hasFile(data.military_certificate_file, "military_certificate_file")) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.fileRequired", path: ["military_certificate_file"] });
    }
    if (data.is_student && !hasFile(data.enrollment_verification_file, "enrollment_verification_file")) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.fileRequired", path: ["enrollment_verification_file"] });
    }

    if (data.is_student) {
      if (!data.enrollment_start_date) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.required", path: ["enrollment_start_date"] });
      }
      if (!data.enrollment_end_date) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.required", path: ["enrollment_end_date"] });
      }
      if (data.enrollment_start_date && data.enrollment_end_date && data.enrollment_end_date <= data.enrollment_start_date) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.enrollmentEndAfterStart", path: ["enrollment_end_date"] });
      }
    }

    if (!data.has_no_education) {
      const hasDiploma = Array.isArray(data.diploma_file) && hasMultiFile(data.diploma_file, "diploma_file");
      const hasTranscript = Array.isArray(data.diploma_transcript_file) && hasMultiFile(data.diploma_transcript_file, "diploma_transcript_file");
      if (!hasDiploma || !hasTranscript) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "validation.diplomaAndTranscriptRequired", path: ["diploma_file"] });
      }
    }
  });
}

export const importWorkerSchema = createImportWorkerSchema();

export type ImportWorkerSchemaValues = z.infer<typeof importWorkerSchema>;
